import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';

// Pages
import LoginPage from './pages/LoginPage';
import SetPasswordPage from './pages/SetPasswordPage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminCities from './pages/admin/AdminCities';
import AdminCategories from './pages/admin/AdminCategories';
import AdminMonuments from './pages/admin/AdminMonuments';
import AdminTicketSlots from './pages/admin/AdminTicketSlots';
import AdminGuides from './pages/admin/AdminGuides';
import AdminReports from './pages/admin/AdminReports';
import AdminSettings from './pages/admin/AdminSettings';
import AdminEmailTemplates from './pages/admin/AdminEmailTemplates';
import AdminProfitLoss from './pages/admin/AdminProfitLoss';
import MemberDashboard from './pages/member/MemberDashboard';
import MemberTickets from './pages/member/MemberTickets';
import MemberDownloads from './pages/member/MemberDownloads';
import MemberGuideRequest from './pages/member/MemberGuideRequest';
import MemberFinance from './pages/member/MemberFinance';
import MemberLedger from './pages/member/MemberLedger';
import MemberInvestments from './pages/member/MemberInvestments';
import UserProfile from './pages/member/UserProfile';
import ExternalDashboard from './pages/external/ExternalDashboard';
import GuideDashboard from './pages/guide/GuideDashboard';
import GuideFinance from './pages/guide/GuideFinance';

// Public Pages
import GuideJoinPage from './pages/public/GuideJoinPage';
import GuideApplicationStatusPage from './pages/public/GuideApplicationStatusPage';
import GuideVerifyEmailPage from './pages/public/GuideVerifyEmailPage';

// Admin Pages
import AdminGuideApplications from './pages/admin/AdminGuideApplications';

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Redirect to appropriate dashboard based on role
    if (user?.role === 'admin') return <Navigate to="/admin" replace />;
    if (user?.role === 'guide') return <Navigate to="/guide" replace />;
    if (user?.role === 'user') {
      return user?.user_type === 'member' 
        ? <Navigate to="/member" replace />
        : <Navigate to="/external" replace />;
    }
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Role-based redirect from root
const RootRedirect = () => {
  const { user, loading, isAuthenticated } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (user?.role === 'admin') return <Navigate to="/admin" replace />;
  if (user?.role === 'guide') return <Navigate to="/guide" replace />;
  if (user?.role === 'user') {
    return user?.user_type === 'member' 
      ? <Navigate to="/member" replace />
      : <Navigate to="/external" replace />;
  }
  
  return <Navigate to="/login" replace />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/set-password" element={<SetPasswordPage />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      
      {/* Guide Application Routes (Public) */}
      <Route path="/guide/join" element={<GuideJoinPage />} />
      <Route path="/guide/application-status/:applicationId" element={<GuideApplicationStatusPage />} />
      <Route path="/guide/verify-email" element={<GuideVerifyEmailPage />} />
      
      {/* Root redirect */}
      <Route path="/" element={<RootRedirect />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminUsers />
        </ProtectedRoute>
      } />
      <Route path="/admin/cities" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminCities />
        </ProtectedRoute>
      } />
      <Route path="/admin/categories" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminCategories />
        </ProtectedRoute>
      } />
      <Route path="/admin/monuments" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminMonuments />
        </ProtectedRoute>
      } />
      <Route path="/admin/ticket-slots" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminTicketSlots />
        </ProtectedRoute>
      } />
      <Route path="/admin/guides" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminGuides />
        </ProtectedRoute>
      } />
      <Route path="/admin/reports" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminReports />
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminSettings />
        </ProtectedRoute>
      } />
      <Route path="/admin/email-templates" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminEmailTemplates />
        </ProtectedRoute>
      } />
      <Route path="/admin/profit-loss" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminProfitLoss />
        </ProtectedRoute>
      } />
      <Route path="/admin/guide-applications" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminGuideApplications />
        </ProtectedRoute>
      } />
      
      {/* Member Routes */}
      <Route path="/member" element={
        <ProtectedRoute allowedRoles={['user']}>
          <MemberDashboard />
        </ProtectedRoute>
      } />
      <Route path="/member/tickets" element={
        <ProtectedRoute allowedRoles={['user']}>
          <MemberTickets />
        </ProtectedRoute>
      } />
      <Route path="/member/downloads" element={
        <ProtectedRoute allowedRoles={['user']}>
          <MemberDownloads />
        </ProtectedRoute>
      } />
      <Route path="/member/guide-request" element={
        <ProtectedRoute allowedRoles={['user']}>
          <MemberGuideRequest />
        </ProtectedRoute>
      } />
      <Route path="/member/finance" element={
        <ProtectedRoute allowedRoles={['user']}>
          <MemberFinance />
        </ProtectedRoute>
      } />
      <Route path="/member/ledger" element={
        <ProtectedRoute allowedRoles={['user']}>
          <MemberLedger />
        </ProtectedRoute>
      } />
      <Route path="/member/investments" element={
        <ProtectedRoute allowedRoles={['user']}>
          <MemberInvestments />
        </ProtectedRoute>
      } />
      <Route path="/member/profile" element={
        <ProtectedRoute allowedRoles={['user']}>
          <UserProfile />
        </ProtectedRoute>
      } />
      
      {/* External Routes - reuse member components with restrictions */}
      <Route path="/external" element={
        <ProtectedRoute allowedRoles={['user']}>
          <ExternalDashboard />
        </ProtectedRoute>
      } />
      <Route path="/external/tickets" element={
        <ProtectedRoute allowedRoles={['user']}>
          <MemberTickets />
        </ProtectedRoute>
      } />
      <Route path="/external/downloads" element={
        <ProtectedRoute allowedRoles={['user']}>
          <MemberDownloads />
        </ProtectedRoute>
      } />
      <Route path="/external/ledger" element={
        <ProtectedRoute allowedRoles={['user']}>
          <MemberLedger />
        </ProtectedRoute>
      } />
      <Route path="/external/profile" element={
        <ProtectedRoute allowedRoles={['user']}>
          <UserProfile />
        </ProtectedRoute>
      } />
      
      {/* Guide Routes */}
      <Route path="/guide" element={
        <ProtectedRoute allowedRoles={['guide']}>
          <GuideDashboard />
        </ProtectedRoute>
      } />
      <Route path="/guide/finance" element={
        <ProtectedRoute allowedRoles={['guide']}>
          <GuideFinance />
        </ProtectedRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="app-container">
          <AppRoutes />
          <Toaster position="top-right" richColors closeButton />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
